import { useMemo } from 'react';
import { Lock, Volume2, FlaskConical } from 'lucide-react';
import { useLectureStore } from '../store/useLectureStore';
import { useAuthStore } from '../../../stores/authStore';
import { isSlideLocked } from '../lib/slideLock';

// Vertical list of slides with played / covered / active state and click-to-jump.
// Implements "advance-when-covered" gating: a slide is reachable once every
// earlier slide whose advance_rule requires it has been covered (audio ended or
// widget complete). Already-covered slides and earlier slides are always open.
//
// A subtle timeline spine threads the markers and fills up to the active slide,
// so the rail reads as a journey through the lecture rather than a flat list.
export default function SlideProgressRail() {
  const slides = useLectureStore((s) => s.slides);
  const currentSlideIndex = useLectureStore((s) => s.currentSlideIndex);
  const completedSlides = useLectureStore((s) => s.completedSlides);
  const goToSlide = useLectureStore((s) => s.goToSlide);
  const audioPosition = useLectureStore((s) => s.audioPosition);
  const audioForSlide = useLectureStore((s) => s.audioForSlide);

  // Admins / super-admins bypass all advance-when-covered gating: every slide is
  // freely clickable for content review.
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);

  const isCovered = (i: number) =>
    slides[i] ? completedSlides.includes(slides[i].id) : false;

  // Live narration progress (0..1) for the active slide — drives the thin fill
  // on the active marker so the rail feels synchronised with the audio.
  const activeProgress = useMemo(() => {
    const ms = audioForSlide(currentSlideIndex)?.duration_ms;
    const dur = ms ? ms / 1000 : 0;
    if (!dur) return 0;
    return Math.max(0, Math.min(1, audioPosition / dur));
  }, [audioForSlide, currentSlideIndex, audioPosition]);

  // The rule now lives in ../lib/slideLock so the rail is not the only thing
  // that enforces it — the store's navigation actions apply the same function,
  // which is what stops the transport's Next button from stepping over a lock.
  const isLocked = (i: number): boolean =>
    isSlideLocked({ slides, completedSlides, currentSlideIndex, isAdmin }, i);

  const coveredCount = slides.filter((_, i) => isCovered(i)).length;

  return (
    <nav className="flex h-full flex-col overflow-y-auto p-2" aria-label="Slides">
      <div className="flex items-baseline justify-between px-2 pb-2 pt-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ws-ink-3">
          Slides
        </span>
        <span className="text-[10px] tabular-nums text-ws-ink-4">
          {coveredCount}/{slides.length}
        </span>
      </div>

      <ol className="relative space-y-0.5" role="list">
        {slides.map((slide, i) => {
          const active = i === currentSlideIndex;
          const covered = isCovered(i);
          const locked = isLocked(i);
          const hasWidget = (slide.widget_refs?.length ?? 0) > 0;
          // The spine segment leading INTO this marker is "filled" once we've
          // moved past the previous slide.
          const spineFilled = i <= currentSlideIndex;
          const isLastVisualConnector = i < slides.length - 1;

          return (
            <li key={slide.id} className="relative">
              <button
                onClick={() => !locked && goToSlide(i)}
                disabled={locked}
                aria-current={active ? 'true' : undefined}
                title={slide.heading || `Slide ${i + 1}`}
                className={`group relative flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                  active
                    ? 'bg-ws-accent font-medium text-white'
                    : locked
                    ? 'cursor-not-allowed text-ws-ink-4'
                    : covered
                    ? 'bg-ws-success-quiet text-ws-ink-2 hover:bg-ws-surface-2'
                    : 'text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {/* Timeline spine: a vertical hairline connecting markers,
                    filled in accent up to / including the current slide. */}
                {isLastVisualConnector && (
                  <span
                    aria-hidden
                    className={`absolute left-[18px] top-[26px] h-[calc(100%-14px)] w-px ${
                      spineFilled ? 'bg-ws-accent/40' : 'bg-ws-border'
                    }`}
                  />
                )}

                {/* Status marker — the slide NUMBER stays visible always; a
                    completed slide is signalled by the green marker fill (+ the
                    tinted row), never by hiding the number behind a checkmark. */}
                <span
                  className={`relative z-10 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold ring-2 ring-ws-rail transition-colors ${
                    covered
                      ? 'bg-ws-success-solid text-ws-on-success'
                      : active
                      ? 'bg-white text-ws-accent'
                      : 'bg-ws-surface-2 text-ws-ink-3'
                  }`}
                >
                  {locked ? <Lock size={11} /> : String(i + 1).padStart(2, '0')}
                </span>

                <span className="line-clamp-2 flex-1 leading-tight">
                  {slide.heading || `Slide ${i + 1}`}
                </span>

                {hasWidget && (
                  <FlaskConical
                    size={12}
                    className={`shrink-0 ${active ? 'text-white/80' : 'text-ws-ink-4'}`}
                    aria-label="Has interactive widget"
                  />
                )}
                {active && (
                  <Volume2 size={12} className="shrink-0 text-white" />
                )}
              </button>

              {/* Live narration progress under the active (uncovered) slide. */}
              {active && !covered && activeProgress > 0 && (
                <div className="mx-2 mb-0.5 ml-9 h-0.5 overflow-hidden rounded-full bg-ws-surface-2">
                  <div
                    className="h-full rounded-full bg-ws-accent transition-[width] duration-200 ease-linear"
                    style={{ width: `${activeProgress * 100}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
